import React, {FC, useEffect, useMemo, useState} from "react";
import {addressService, tokenService} from "../../../store/actions";
import {Link, useParams, useSearchParams} from "react-router-dom";
import useCommon from "../../../common/hooks/useCommon";
import {
    formatNumber,
    formatTimestamp,
    formatUnit,
    getFiat,
    getPlural,
    getTimeFromNow,
    getTimeLang,
    serializeQuery,
    toGwei,
    toXDCNumber,
    truncate
} from "../../../common/utils/function";
import BTable2 from "../../../components/b-table2";

import AddressName from "../address-name";
import BTooltip from "../../../components/b-tooltip";
import TxStatus from "../tx-status";
import BPrice from "../b-price";
import {useTranslation} from "react-i18next";
import MethodName from "../method-name";
import TokenImage from "../token-image";
import BCopy from "../../../components/b-copy";
import CSVConverter from "../../../components/b-CSV-converter";
import InOut from "../in-out";
import {useSelector} from "react-redux";

interface XRC720TokenTransferListProps {
    count: number;
    type?: any;
    stats?: any;

}

interface TransactionListData {
    tx_hash: string;
    block: number;
    timestamp: string | number;
    extraMark: string;
    from: any;
    to: any;
    valuenumber: string | number;
    gas_price: string | number;
    total: any;
}

const XRC720TokenTransferList: FC<XRC720TokenTransferListProps> = (props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const addressParams = searchParams.get("address");
    const {address, token, holder} = useParams();
    const {getXDC, get0x} = useCommon();
    const {t, i18n} = useTranslation();
    const [isUTC, setIsUTC] = useState(false);
    const [isValue, setIsValue] = useState(false);
    const _stats = useSelector((e: Object) => (e as any)?.common?.stats);

    const [transactionList, setTransactionList] = useState<TransactionListData[]>(
        []
    );
    const [newTransactionList, setNewTransactionList] = useState<TransactionListData[]>(
        []
    );
    const [transferCount, setTransferCount] = useState({
        token_holders_count: "",
        transfer_count: "",
    });
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState<any>({
        limit: 50,
        page: 1,
    });
    const [totalRecords, setTotalRecords] = useState<any>(0);
    const handle = {
        pagination: (payload: any) => {
            setPagination(payload);
        }
    };
    const handleButtonClick = () => {
        setIsUTC(!isUTC);
    };
    const formatFiat = (
        value: string | number | null | undefined,
        exchangeRate: number | string | null | undefined,
        coinPrice: number | string | null | undefined,
        decimal: string | number | null | undefined = 18
    ): string => {
        if (!value || (!coinPrice)) return "$0.00";
        const safeDecimal = typeof decimal === "number" ? decimal : 18;
        const xdcValue = parseFloat(toXDCNumber(value, safeDecimal, 2));
        const exchange = exchangeRate ? parseFloat(String(exchangeRate)) : parseFloat(String(coinPrice));
        const amount = xdcValue * exchange;
        return getFiat(amount || "0.00");
    };

    const getBlocks = async () => {
        try {
            setLoading(true);
            setTransactionList([]);
            setNewTransactionList([]);

            let params = {type: props?.type ?? "ERC-20", ...pagination};

            if (address || addressParams) {
                const baseAddress = address || addressParams;
                if (token && token !== "undefined") {
                    params = {...params, token: token};
                }
                const queryString = serializeQuery(params);

                try {
                    const response = await addressService.getTokenTransfers(baseAddress, queryString);
                    const {items, count} = response;
                    setTransactionList(items);
                    setNewTransactionList(transformTransactions(items));
                    setTotalRecords(count);
                    setLoading(false)
                } catch (error) {
                    setLoading(false)
                    console.log(error);
                } finally {
                    setLoading(false);
                }
            } else {
                try {
                    const response = await tokenService.getTokenTransfers(token || holder, serializeQuery(params));
                    const {items, count} = response;
                    setTransactionList(items);
                    setNewTransactionList(transformTransactions(items));
                    setTotalRecords(count);
                    setLoading(false)
                } catch (error) {
                    setLoading(false)
                    console.log(error);
                } finally {
                    setLoading(false);
                }
            }
        } catch (err) {
            console.log(err);
            setLoading(false);
        }
    };

    const getTransferHodlerCount = async () => {
        try {
            setLoading(true);
            tokenService
                .getTransferHodlerCount(token || holder) 
                .then((response) => {
                    setTransferCount(response);
                    setLoading(false);
                })
                .catch((e) => setLoading(false));
        } catch (err) {
            console.log(err);
        }
    };
    useEffect(() => {
        getBlocks();
        getTransferHodlerCount();
    }, [pagination, address, addressParams, isValue, isUTC]);

    const transformTransactions = (transactions: TransactionListData[]): TransactionListData[] => {
        return transactions.map((txn) => {
            const quality = toXDCNumber(txn?.total?.value, txn?.total?.decimals, 0)
            const newQuanlity = quality.toString();
            const val = toXDCNumber(txn?.total?.value, txn?.total?.decimals, 6)
            const newVal = val.toString();
            return {
                ...txn,
                newQuanlity,
                newVal,
            };
        });
    };

    let fileNameAddress = token || "unknown";
    const fileName = `export-token-transfers-${fileNameAddress}.csv`;

    const headerList = ['tx_hash', 'method_name', 'timestamp', 'token.type', 'from.hash', 'to.hash', 'newVal', 'newQuanlity'];
    const customHeaderList = {
        'tx_hash': 'Hash',
        'method_name': 'Method',
        'timestamp': 'Timestamp',
        'token.type': 'Type',
        'from.hash': 'From',
        'to.hash': 'To',
        'newVal': 'Value',
        'newQuanlity': 'Quantity'
    }

    const header = useMemo(() => {
        return [
            {
                title: (
                    <BTooltip text={t("See preview of the transaction details.")}>
                        <i className="bi bi-question-circle me-2 ps-2"></i>
                    </BTooltip>
                ),
                param: "header",
                render: (data: any) => (
                    <div>
                        <BTooltip
                            light={true}
                            text={
                                <React.Fragment>
                                    <div className="bs-transaction-additional-info p-2">
                                        <h2 className="bs-card-header-title">
                                            {t("Additional Info")}
                                        </h2>
                                        <div className="py-2">
                                            <p className="bs-transaction-header">{t("Status")}:</p>
                                            <p className="bs-transaction-content">
                                                <TxStatus status={data?.status}/>{" "}
                                                {getPlural("block", data?.confirmations)}{" "}
                                                {t("confirmation")}
                                            </p>
                                        </div>
                                        <hr/>
                                        <div className="py-2">
                                            <p className="bs-transaction-header">
                                                {t("Transaction Action")}:
                                            </p>
                                            <p className="bs-transaction-content">
                                                {t("Transfer")}:{" "}
                                                {(toXDCNumber(data?.total?.value, data?.total?.decimals, 0))}
                                                <br/>
                                                {t("To")}:{" "}
                                                <Link to={`/address/${get0x(data?.to?.hash)}`}>
                                                    <AddressName data={data?.to}/>
                                                </Link>
                                            </p>
                                        </div>
                                        <hr/>
                                        <div className="py-2">
                                            <p className="bs-transaction-header">{t("Transaction Fee")}:</p>
                                            <p className="bs-transaction-content">
                                                {formatUnit(toXDCNumber(data?.gas_price * data?.gas_used, 18))}
                                                <BPrice
                                                    value={data?.fee?.value}
                                                    coingeckoID={process.env.REACT_APP_COINGECKO_ID}
                                                    timestamp={getTimeFromNow(data?.timestamp)}
                                                    XDCUSD="XDCUSD"
                                                    decimal={0}
                                                    type=""
                                                />
                                            </p>
                                        </div>
                                        <hr/>
                                        <div className="py-2">
                                            <p className="bs-transaction-header">{t("Gas Info")}:</p>
                                            <p className="bs-transaction-content">
                                                {formatNumber(data?.gas_limit)} |{" "}
                                                {formatNumber(data?.gas_used)}
                                                <br/>
                                                {formatUnit(toXDCNumber(data?.gas_price, 18))} (
                                                {toGwei(data?.gas_price)} Gwei)
                                            </p>
                                        </div>
                                        <hr/>
                                        <div className="py-2">
                                            <p className="bs-transaction-header">{t("Nonce")}:</p>
                                            <p className="bs-transaction-content">{data?.nonce} ({t("in the position")} {data?.position})</p>
                                        </div>
                                        <hr/>
                                        <div className="py-2">
                                            <Link to={`/tx/${get0x(data?.hash)}`}>
                                                {t("See more details")} &nbsp;
                                                <i className="bi bi-arrow-up-right"></i>
                                            </Link>
                                        </div>
                                    </div>
                                </React.Fragment>
                            }
                            options={{position: ["right center", "right top", "right bottom"], on: ["click"],}}
                        >
                            <button className="btn btn-sm btn-white fs-70x content-center btn-hov" style={{width: "1.75rem", height: "1.75rem"}}><i className="bi bi-eye"></i>
                            </button>
                        </BTooltip>
                    </div>
                ),
            },
            {
                title: `${t("Tx Hash")}`,
                param: "tx_hash",
                tooltip: (data: any) => <Link to={`/tx/${get0x(data?.tx_hash)}`}>{getXDC(data?.tx_hash)}</Link>,
                render: (data: any) => <Link to={`/tx/${get0x(data?.tx_hash)}`}>   {truncate(get0x(data?.tx_hash))}
                    &nbsp;<BCopy value={get0x(data?.tx_hash)} tooltip={`${t("Transaction Hash")}`}/></Link>,
            },
            {
                title: `${t("Block")}`,
                param: "block_number",
                tooltip: (data: any) => <Link to={`/block/${data?.block_number}`}>Block {data?.block_number}</Link>,
                render: (data: any) => <Link to={`/block/${(data?.block_number)}`}>   {data?.block_number}
                    &nbsp;</Link>,
            },
            {
                title: `${t("Method")}`,
                param: "method",
                render: (data: any) => <MethodName data={data}/>,
            },
            {
                title: (<a href="javascript:" onClick={handleButtonClick} data-bs-trigger="hover">{isUTC ? t("Date Time (UTC)") : t("Age")}</a>),
                param: "timestamp",
                tooltip: (data: any) => isUTC ? getTimeLang(data?.timestamp, t) : formatTimestamp(data?.timestamp),
                render: (data: any) => isUTC ? formatTimestamp(data?.timestamp) : getTimeLang(data?.timestamp, t),
            },
            {
                title: `${t("From")}`,
                param: "tx_hash",
                headerStyle: {minWidth: "130px"},
                copy: (data: any) => getXDC(data?.from?.hash),
                tooltip: (data: any) =>
                    data?.from?.hash === (address || get0x(addressParams || "") || holder)?.toLowerCase() ? (
                        getXDC(data?.from?.hash)
                    ) : (
                        <Link to={`/token/${get0x(data?.token?.address)}?address=${get0x(data?.from?.hash)}`}>
                            {getXDC(data?.from?.hash)}
                        </Link>
                    ),
                render: (data: any) =>
                    data?.from?.hash === (address || get0x(addressParams || "") || holder)?.toLowerCase() ? (
                        <AddressName data={data?.from}/>
                    ) : (
                        <Link to={`/token/${get0x(data?.token?.address)}?address=${get0x(data?.from?.hash)}`}>
                            <AddressName data={data?.from}/>
                        </Link>
                    ),
            },
            {
                title: "",
                param: "from",
                headerStyle: {minWidth: "60px"},
                render: (data: any) => (
                    <React.Fragment>
                        {[get0x(addressParams || address)?.toLowerCase(), get0x(token)?.toLowerCase(), get0x(holder)?.toLowerCase()].includes(get0x(data?.from?.hash)?.toLowerCase()) ? <InOut out/> : <InOut/>}
                    </React.Fragment>
                ),
            },
            {
                title: `${t("To")}`,
                param: "tx_hash",
                headerStyle: {minWidth: "130px"},
                copy: (data: any) => getXDC(data?.to?.hash),
                tooltip: (data: any) =>
                    data?.to?.hash === (address || get0x(addressParams || "") || holder)?.toLowerCase() ? (
                        getXDC(data?.to?.hash)
                    ) : (
                        <Link to={`/token/${get0x(data?.token?.address)}?address=${get0x(data?.to?.hash)}`}>
                            {getXDC(data?.to?.hash)}
                        </Link>
                    ),
                render: (data: any) =>
                    data?.to?.hash === (address || get0x(addressParams || "") || holder)?.toLowerCase() ? (
                        <AddressName data={data?.to}/>
                    ) : (
                        <Link to={`/token/${get0x(data?.token?.address)}?address=${get0x(data?.to?.hash)}`}>
                            <AddressName data={data?.to}/>
                        </Link>
                    ),
            }
            ,

            {
                visible: props?.type === "ERC-20",
                title: `${t("Quantity")}`,
                param: "tx_hash",
                render: (data: any) => toXDCNumber(data?.total?.value, data?.total?.decimals, 18),
            },
            {
                visible: !!holder || !!address,
                title: (<a href="javascript:" onClick={() => setIsValue(!isValue)}data-bs-trigger="hover"> {isValue ? t("Value (USD)") : t("Amount")}</a>),
                headerStyle: {minWidth: "130px"},
                param: "tx_hash",
                render: (data: any) =>
                    <span onClick={() => setIsValue(!isValue)}>
                        {isValue  ? formatFiat(data?.total?.value, data?.token?.exchange_rate , _stats?.coin_price, data?.total?.decimals) : toXDCNumber(data?.total?.value, data?.total?.decimals, 6)}
                </span>
            },
            {
                visible: props?.type === "ERC-721" || props?.type === "ERC-1155",
                title: `${t("Item")}`,
                param: "item",
                render: (data: any) =>
                    data?.total?.token_id ? <Link to={`/nft/${data?.token?.address}/${data?.total?.token_id}`} className="d-flex align-items-center">
                        <TokenImage image={data?.token?.icon_url} nft symbol={data?.token?.symbol} params={{width: 32, className: "me-1"}}/>
                        #{" "}{data?.total?.token_id ? truncate(data?.total?.token_id ?? "") : ""}
                    </Link> : "",
            },
            {
                visible: !!holder || !!address,
                title: `${t("Token")}`,
                param: "tx_hash",
                render: (data: any) => <p className="text-truncate">
                    {data?.token?.name ? (<Link to={`/token/${get0x(data?.token?.address)}`} className="d-flex align-items-center">
                        <TokenImage image={data?.token?.icon_url} symbol={data?.token?.symbol} params={{width: "20px", className: "me-1"}}/>
                        {truncate(data?.token?.name || "")}({data?.token?.symbol})
                    </Link>) : ("-")}
                </p>,
            },
            {
                visible: !!holder,
                title: `${t("Details")}`,
                param: "tx_hash",
                render: (data: any) => <Link to={`/nft/${data?.token?.address}/${data?.total?.token_id}`}>View NFT</Link>,
            },
        ];
    }, [props, pagination, address, token, holder, isValue, isUTC]);
    return (
        <BTable2
            title={<div className="d-flex justify-content-between align-items-center">
                <div>
                    <p className="mb-0 font-b">
                        <i className="bi bi-sort-down"></i>
                        {addressParams ? (
                                <React.Fragment>
                    {`A total of ${totalRecords} transactions found`} <br/>
                                </React.Fragment>
                            ) :
                            (
                                <React.Fragment>
                                    {`Latest 25 ${process.env.REACT_APP_TOKEN_TYPE}-20 Token Transfer Events`} &nbsp;
                                    (<Link to={`/tokentxns?a=${get0x(address || token)}`}>View All</Link>)
                                </React.Fragment>
                            )}
                        <br/>
                    </p>
                </div>
                <div>
                    <CSVConverter data={newTransactionList} fileName={fileName} headers={headerList} customHeaders={customHeaderList}/>
                    <button className="btn btn-sm bs-btn me-2">
                        <Link to={`/advanced-filter?tkn=${get0x(address)}`}>
                            <i className="bi bi-funnel me-2"></i> Advanced Filter
                        </Link>
                    </button>
                </div>
            </div>}
            pagination={pagination}
            totalRecords={totalRecords}
            onPagination={handle.pagination}
            loading={loading}
            list={transactionList}
            header={header}
            // enableTitle={false}

            // sort={filter?.sort}
            // onSort={handle.sort}
            options={[50, 100]}
        />
    );
};

export default XRC720TokenTransferList;
